import { BdDocType, BdGap, BdQuestion, BdReply, BdResults, BdShare, BdShareItem, BdShareKind, BdUnclear } from "../../types/bd/Bd";
import { findingKey, findingsOf, FindingRef, pickOf, shareKindOf } from "./bdPicks";

/**
 * The points we send back to the other side (Share back): every finding
 * picked "Ask them", plus the person's own points. Questions go as questions;
 * for partner documents and contracts a gap's suggested wording goes as a
 * correction and its note as a comment. Each point gets a short reference
 * (Q1, C2, N3) that goes at the start of its comment, so their replies can be
 * matched later even if they reword things. Untouched points follow the
 * findings; edited ones keep the person's wording. A point stays on the list
 * once made, so its reference holds; whether it is sent follows the pick.
 */

interface ShareSource { src: string; kind: BdShareKind; q: string; note: string; repl: string; inc: boolean; explicit: boolean; }

const lowerFirst = (t: string): string => t.charAt(0).toLowerCase() + t.slice(1);
const KIND_LETTER: Record<BdShareKind, string> = { Question: "Q", Correction: "C", Comment: "N" };

/** What each finding would send them, and whether its pick sends it now. */
export function shareSources(results: BdResults, docType: BdDocType): ShareSource[] {
    const src = (f: FindingRef, kind: BdShareKind, q: string, note: string, repl = ""): ShareSource =>
        ({ src: f.key, kind, q, note, repl, inc: pickOf(f.kind, f.item, docType) === "ask", explicit: f.item.pick === "ask" });
    return [
        ...findingsOf(results, "gap").map((f) => {
            const g = f.item as BdGap;
            const kind = shareKindOf("gap", g, docType);
            return kind === "Question"
                ? src(f, kind, g.q, g.q ? `Could you clarify: “${g.q}”?` : `Could you clarify: ${lowerFirst(g.t)}`)
                : src(f, kind, g.q, g.ext || g.act || g.t, kind === "Correction" ? g.fix ?? "" : "");
        }),
        ...findingsOf(results, "question").filter((f) => (f.item as BdQuestion).share !== false).map((f) => src(f, "Question", (f.item as BdQuestion).q || "", f.item.t)),
        ...findingsOf(results, "unclear").map((f) => src(f, "Question", (f.item as BdUnclear).q || "", `Could you clarify: ${lowerFirst(f.item.t)}`)),
        ...findingsOf(results, "missing").map((f) => src(f, "Question", "", `Could you send us ${lowerFirst(f.item.t)}?`)),
    ];
}

/**
 * Bring the share list up to date with the findings (pure: returns a new list).
 * @param share - The saved list, or null.
 * @param results - The findings.
 * @param docType - The kind of document.
 */
export function syncShare(share: BdShare | null | undefined, results: BdResults | null | undefined, docType: BdDocType): BdShare {
    const sh: BdShare = share ? { items: share.items.map((x) => ({ ...x })), removed: [...share.removed], rc: { ...share.rc }, n: share.n } : { items: [], removed: [], rc: {}, n: 0 };
    if (!results) return sh;
    const nextRef = (kind: BdShareKind): string => {
        const l = KIND_LETTER[kind];
        sh.rc[l] = (sh.rc[l] ?? 0) + 1;
        return `${l}${sh.rc[l]}`;
    };
    const taken = (q: string, self?: BdShareItem) => !!q && sh.items.some((it) => it !== self && it.q && (it.q.includes(q) || q.includes(it.q)));
    const sources = shareSources(results, docType);
    sources.forEach((s) => {
        const ex = sh.items.find((it) => it.src === s.src);
        if (ex) {
            ex.inc = s.inc;
            if (!ex.edited) { ex.note = s.note; ex.repl = s.repl; ex.q = ex.q || s.q; }
            return;
        }
        // points removed on the old Share back tab stay out until someone picks Ask them again
        if (!s.inc || (sh.removed.includes(s.src) && !s.explicit)) return;
        if (s.kind === "Question" && s.src.startsWith("u:") && !s.explicit && taken(s.q)) return;
        const q = s.kind === "Question" && s.q && taken(s.q) ? "" : s.q;
        sh.n += 1;
        sh.items.push({ id: `sh${sh.n}`, ref: nextRef(s.kind), src: s.src, kind: s.kind, q, note: s.note, repl: s.repl, inc: true, edited: false });
    });
    // findings that are gone stop being sent unless someone edited them
    const live = new Set(sources.map((s) => s.src));
    sh.items = sh.items.filter((it) => it.src.startsWith("mine:") || it.edited || live.has(it.src));
    sh.items.forEach((it) => { if (!it.ref) it.ref = nextRef(it.kind); });
    return sh;
}

/** Add a point of the person's own. */
export function addSharePoint(share: BdShare, point: { kind: BdShareKind; q: string; note: string; repl: string }): BdShare {
    const next: BdShare = { ...share, items: [...share.items], rc: { ...share.rc } };
    const l = KIND_LETTER[point.kind];
    next.rc[l] = (next.rc[l] ?? 0) + 1;
    next.n += 1;
    next.items.push({ id: `sh${next.n}`, ref: `${l}${next.rc[l]}`, src: `mine:${next.n}`, kind: point.kind, q: point.q, note: point.note, repl: point.repl, inc: true, edited: true });
    return next;
}

/** A point is still open when they haven't answered it (or deleted our comment). */
export const isOpenPoint = (reply: BdReply | null | undefined, item: BdShareItem): boolean => {
    const r = reply?.res?.[item.id];
    return !r || r.st === "open" || r.st === "removed";
};

/** "5 questions" / "3 questions, 2 suggested changes and 1 comment". */
export function shareCountText(items: BdShareItem[]): string {
    const n = (k: BdShareKind) => items.filter((x) => x.kind === k).length;
    const parts = ([["Question", "question"], ["Correction", "suggested change"], ["Comment", "comment"]] as Array<[BdShareKind, string]>)
        .filter(([k]) => n(k)).map(([k, w]) => `${n(k)} ${w}${n(k) === 1 ? "" : "s"}`);
    if (!parts.length) return "nothing";
    return parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

/**
 * The points as an email list, grouped by topic, each with its reference so
 * an answer by email can be matched too.
 * @param items - The points to list (those sent, or only the open ones).
 * @param results - The findings (a question's topic).
 * @param categories - The topics in their order (BD settings).
 */
export function shareEmailList(items: BdShareItem[], results: BdResults | null, categories: string[]): string {
    const topic = (x: BdShareItem): string => {
        if (x.kind === "Correction") return "Suggested changes to your text";
        if (x.kind === "Comment") return "Comments";
        return results?.questions.find((q, i) => findingKey("question", q, i) === x.src)?.cat || "Other questions";
    };
    const order = [...categories, "Other questions", "Suggested changes to your text", "Comments"];
    const groups = Array.from(new Set(items.map(topic))).sort((a, b) => (order.indexOf(a) + 1 || 99) - (order.indexOf(b) + 1 || 99));
    return groups.map((g) => `${g}\n${items.filter((x) => topic(x) === g).map((x) => `${x.ref}. ${x.note}${x.kind === "Correction" && x.repl ? ` (suggested wording: “${x.repl}”)` : ""}`).join("\n")}`).join("\n\n");
}
